import type { Die, DieId, SinType } from "@cloudnest/carnival-of-sins-engine";
import { polarToPercent, seatAngle } from "./utils/seat-geometry";

interface TablePlayer {
  readonly playerId: string;
  readonly name: string;
}

interface CarnivalTableProps {
  readonly players: readonly TablePlayer[];
  readonly seatingOrder: readonly string[];
  readonly devilCardHolderId: string;
  /** Whose turn it currently is to reveal/resolve, if any (SECRET_SELECT has none). */
  readonly activePlayerId?: string | null;
  readonly dice: readonly Die[];
  /** When set, dice in `selectableDieIds` become clickable (live GameScreen picking a target). */
  readonly onDieClick?: (die: Die) => void;
  readonly selectableDieIds?: ReadonlySet<DieId>;
  readonly selectedDieIds?: ReadonlySet<DieId>;
}

const SIN_COLOR_CLASS: Readonly<Record<SinType, string>> = {
  greed: "sin-greed",
  gluttony: "sin-gluttony",
  envy: "sin-envy",
  lust: "sin-lust",
  pride: "sin-pride",
  wrath: "sin-wrath",
  sloth: "sin-sloth",
};

function initials(name: string): string {
  return name.slice(0, 1).toUpperCase();
}

export function CarnivalTable({
  players,
  seatingOrder,
  devilCardHolderId,
  activePlayerId,
  dice,
  onDieClick,
  selectableDieIds,
  selectedDieIds,
}: CarnivalTableProps) {
  const nameById = new Map(players.map((p) => [p.playerId, p.name]));

  return (
    <div
      className="cos-table-wrap"
      data-testid="carnival-of-sins-board"
      data-round-players={seatingOrder.length}
    >
      <div className="cos-table" />

      {seatingOrder.map((playerId, index) => {
        const angle = seatAngle(index, seatingOrder.length);
        const { left, top } = polarToPercent(angle, 1);
        return (
          <div
            key={playerId}
            className="cos-seat"
            data-testid={`carnival-of-sins-seat-${playerId}`}
            data-active={playerId === activePlayerId}
            data-devil={playerId === devilCardHolderId}
            style={{ left, top }}
          >
            <div className="cos-seat-avatar">
              {initials(nameById.get(playerId) ?? playerId)}
            </div>
            <div className="cos-seat-name">
              {nameById.get(playerId) ?? playerId}
            </div>
          </div>
        );
      })}

      {dice.map((die) => {
        const { left, top } = polarToPercent(
          die.position.angle,
          die.position.radius,
        );
        const locked = die.lockedBy !== null;
        const shapeClass = `cos-die cos-die--${die.id === "white" ? "d6 cos-die--white" : die.id}`;
        const sinClass = die.lockedBy
          ? SIN_COLOR_CLASS[die.lockedBy.sin]
          : undefined;
        const selectable = selectableDieIds?.has(die.id) ?? false;
        const selected = selectedDieIds?.has(die.id) ?? false;
        const className = [
          shapeClass,
          sinClass,
          selectable ? "cos-die--selectable" : undefined,
          selected ? "cos-die--selected" : undefined,
        ]
          .filter(Boolean)
          .join(" ");
        const style = {
          left,
          top,
          ...(sinClass
            ? { ["--cn-die-owner" as string]: "var(--cn-sin-color)" }
            : {}),
        };
        const testProps = {
          "data-testid": `carnival-of-sins-die-${die.id}`,
          "data-shape": die.id,
          "data-color": die.color,
          "data-locked": locked,
          "data-locked-by": die.lockedBy?.ownerId ?? "none",
          "data-selectable": selectable,
          "data-selected": selected,
        } as const;

        if (onDieClick && selectable) {
          return (
            <button
              key={die.id}
              type="button"
              className={className}
              style={style}
              onClick={() => onDieClick(die)}
              {...testProps}
            >
              {die.value}
            </button>
          );
        }
        return (
          <div key={die.id} className={className} style={style} {...testProps}>
            {die.value}
          </div>
        );
      })}
    </div>
  );
}
